import { useInfiniteQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../../constants'

import { StoresContext } from '../../../provider/StoreProvider'

import { getNoticeList } from '../../../api/notice'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { CenterWrap } from '../../../components/common'
import { Header } from '../../../components/header'
import { Spinner } from '../../../components/spinner/Spinner'
import { StyledLabel } from '../../../components/label'
import { Text } from '../../../components/text'
import { EmptyPage } from '../../../components/exception/EmptyPage'
import { InfinityScroll } from '../../../components/pagingList/InfinityScroll'
import { NotificationList, NotificationItem } from '../../notification/components/List'

const HEAD_INFO = {
  title: '공지사항 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '공지사항 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function NoticeList() {
  const {
    commonStore: { setShowTab },
  } = useContext(StoresContext)

  const { data, isPending, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.noticeList],
    queryFn: async ({ pageParam = 0 }) => await getNoticeList({ offset: pageParam, size: 50 }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  useEffect(() => {
    setShowTab(false)
  }, [])

  if (isPending) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="공지사항" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  if (isError || !data.pages.length) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="공지사항" />
          <EmptyPage content="조회 가능한 공지사항이 없습니다." />
        </Wrap>
      </>
    )
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="공지사항" />
        <StyledContent id="infinity-scroll-target">
          <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
            <NotificationList>
              {data.pages.map(({ id, title, createdDate }) => {
                const isNew = dayjs().diff(dayjs(createdDate), 'day') + 1 < 8

                return (
                  <Link key={id} to={`/notice/${id}`}>
                    <NotificationItem>
                      {isNew ? (
                        <StyledLabel className="notification-type" backgroundColor="error_light">
                          <Text size={12} weight={500} color="error">
                            NEW
                          </Text>
                        </StyledLabel>
                      ) : null}
                      <NotificationItem.Title>{title ?? ''}</NotificationItem.Title>
                      <NotificationItem.Date>{createdDate ?? ''}</NotificationItem.Date>
                    </NotificationItem>
                  </Link>
                )
              })}
            </NotificationList>
          </InfinityScroll>
        </StyledContent>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  padding-top: 8px;
`
